import * as React from 'react'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { useFormikContext } from 'formik'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle } from '@fluentui/react-components'
import { Location } from 'history'

type PendingNavigation = { location: Location; action: string }

/**
 * Warns before leaving the party screen with unsaved changes (AC6). Uses the memory history's
 * `block()` — not `beforeunload`, which doesn't fire in the Office add-in webview.
 *
 * Navigation is allowed through untouched while the form is pristine or while it's submitting, so the
 * post-save return (which happens while `isSubmitting` is still true) is never intercepted. On a
 * blocked navigation a confirmation dialog offers to stay or discard; discarding unblocks and replays
 * the original navigation.
 *
 * Staying on the same path is never a departure, so it is let through: partyGoBack tags the return
 * location with `state.reload` a tick after navigating, and blocking that would replace the real
 * pending navigation with a replace onto the page the user is already looking at - leaving Discard
 * with nothing to do. For the same reason a pending navigation is never overwritten: the first one
 * is the one the user asked for.
 */
export default function UnsavedChangesGuard(): ReactElement {
    const { dirty, isSubmitting } = useFormikContext()
    const history = useHistory()
    const { t: translate } = useTranslation()
    const [pending, setPending] = useState<PendingNavigation>()
    const unblockRef = useRef<() => void>()

    useEffect(() => {
        unblockRef.current = history.block((location, action) => {
            if (!dirty || isSubmitting || location.pathname === history.location.pathname) {
                return undefined // allow
            }
            setPending(current => current ?? { location, action })
            return false // block until the user decides
        })
        return () => unblockRef.current?.()
    }, [dirty, isSubmitting, history])

    const discardAndLeave = () => {
        const target = pending
        setPending(undefined)
        unblockRef.current?.()
        if (target) {
            if (target.action === 'POP') {
                history.goBack()
            } else if (target.action === 'REPLACE') {
                history.replace(target.location)
            } else {
                history.push(target.location)
            }
        }
    }

    return (
        <Dialog open={!!pending} onOpenChange={(_, data) => !data.open && setPending(undefined)}>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>{translate('party.edit.unsaved.title')}</DialogTitle>
                    <DialogContent>{translate('party.edit.unsaved.message')}</DialogContent>
                    <DialogActions>
                        <Button appearance="secondary" onClick={() => setPending(undefined)}>
                            {translate('party.edit.unsaved.stay')}
                        </Button>
                        <Button appearance="primary" onClick={discardAndLeave}>
                            {translate('party.edit.unsaved.discard')}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    )
}
